// web/app/Arrangement.js
//
// The arrangement: a lane of clips per track, the notes of the clip open in
// the piano roll, and audio files a person brings in. Every edit is a request
// to the dispatcher; the drawing follows from what the project then says, on
// the next 'changed'.
import { createTimeline, barBeat } from '../../src/ui/Timeline.js'
import { createPianoRoll } from '../../src/ui/PianoRoll.js'
import { describeNoteTransform } from '../../src/model/NoteTransform.js'
import { describeEuclidean } from '../../src/model/Euclidean.js'
import { LANE_SIZES } from '../../src/model/EditorState.js'
import { preserveFocus } from '../../src/ui/Focus.js'
import { trackKind } from '../../src/model/TrackKind.js'
import { mixable } from '../../src/ui/Mixer.js'
import { describeChain } from '../../src/ui/ChainModel.js'
import { describeRouting } from '../../src/ui/SendsModel.js'
import { spokenValue } from '../../src/ui/Panel.js'
import { createClipActions } from '../../src/ui/ClipActions.js'
import { duplicateTrack } from '../../src/ops/DuplicateTrack.js'
import { describeStart } from '../../src/ui/StartScreen.js'
import { repeatClip } from '../../src/model/ClipLoop.js'
import { chainSwapChanges } from '../../src/ops/ChainReorder.js'
import { splitClip, duplicateClip, trimClip, copyClips, pasteClips } from '../../src/model/ClipEdit.js'

export function createArrangement (ctx) {
  const { document, $, log } = ctx

  const edit = changes => {
    const result = ctx.dispatcher.apply(changes)
    if (!result.ok) log(result.message, 'error')
    return result
  }

  // The first thing done in an empty session, so there may be no dispatcher yet: the click is the gesture that starts audio.
  // The toolbar's Add track and the start screen's "An empty track" are this one function.
  async function addTrack () {
    await ctx.runtime.ensureRunning()
    const result = edit([{ op: 'addTrack' }])
    if (!result.ok) return null
    ctx.selection.set('track', [result.results[0]])
    return result.results[0]
  }

  // Where the next imported audio file goes, while the file picker is open.
  let pendingAudio = null
  // Sources already asked to load for drawing, so a redraw asks once.
  const waveformsRequested = new Set()

  // Each is a request for one clip; the keys and the buttons below both make them.
  const handlers = {
    // The loop handle on a clip, the R key and the menu: the same one change (src/model/ClipLoop.js), so one undo takes back a repeat.
    onRepeat: (id, lengthBeats) => edit([repeatClip(ctx.dispatcher.project.clip(id), lengthBeats)]),
    onStopRepeat: id => edit([{ op: 'setClip', id, loopBeats: null }]),
    onAdd: (trackId, startBeat) => {
      const result = edit([{ op: 'addClip', track: trackId, kind: 'midi', startBeat, lengthBeats: ctx.dispatcher.project.transport.beatsPerBar }])
      if (result.ok) openClip(result.results[0])
    },
    onAddAudio: (trackId, startBeat) => {
      pendingAudio = { trackId, startBeat }
      $('audiofile').click()
    },
    onMove: (id, startBeat) => edit([{ op: 'setClip', id, startBeat }]),
    onResize: (id, lengthBeats) => edit([{ op: 'setClip', id, lengthBeats }]),
    // The timeline has already selected it; the dock shows what that calls for.
    onOpen: id => openClip(id),
    onSetLoop: range => ctx.transport.setLoopRange(range),
    onSeek: beat => ctx.transport.seek(beat),
    onArm: (trackId, on) => {
      const source = ctx.dispatcher.project.track(trackId)?.recordSource ?? 'midi'
      if (source === 'audio') ctx.micIn.arm(trackId, on); else ctx.midiIn.arm(trackId, on)
      ctx.rack.draw()
    },
    onArmSource: (trackId, source) => {
      ctx.dispatcher.apply([{ op: 'setTrack', id: trackId, recordSource: source }])
      ctx.rack.draw()
    },
    // Order is layout, not an edit: no revision, no undo, and the editor graph tells the page.
    onMoveTrack: (trackId, delta) => ctx.dispatcher.project.moveTrack(trackId, delta),
    // Saved with the session: stated on every track of the folder, which is how the editor graph knows a folder.
    onFolderCollapse: (folder, collapsed) => {
      const { project } = ctx.dispatcher
      for (const track of project.tracks) if (project.trackLayout(track.id).folder === folder) project.setTrackLayout(track.id, { folderCollapsed: collapsed })
    },
    // Plus and minus on a track's name: one size taller or shorter, stopping at either end.
    onSizeStep: (trackId, by) => {
      const { laneSize } = ctx.dispatcher.project.trackLayout(trackId)
      const next = LANE_SIZES[LANE_SIZES.indexOf(laneSize) + by]
      // Choosing a size puts a dragged height away, so plus and minus always do what they say.
      if (next) ctx.dispatcher.project.setTrackLayout(trackId, { laneSize: next, laneHeight: null })
    },
    onExpandTrack: trackId => ctx.dispatcher.project.setTrackLayout(trackId, { laneSize: 'small', laneHeight: null }),
    // The grip along a lane's bottom edge: a height of its own, kept with the layout, and put back by a double click.
    onLaneHeight: (trackId, height) => ctx.dispatcher.project.setTrackLayout(trackId, { laneHeight: height }),
    onLaneReset: trackId => ctx.dispatcher.project.setTrackLayout(trackId, { laneHeight: null }),
    // The toolbar's Add track opens the chooser, which has An empty track in it.
    onAddTrack: () => ctx.addTrack.open(),
    onLoops: () => ctx.loops.open(),
    onDuplicateTrack: async trackId => {
      const result = await duplicateTrack(ctx.dispatcher, trackId)
      if (!result.ok) { log(result.message, 'error'); return }
      ctx.selection.set('track', [result.trackId])
      log(`duplicated ${trackId}${result.skipped.length ? `, ${result.skipped.join('; ')}` : ''}`, 'ok')
    },
    onChannel: (trackId, change) => {
      const result = ctx.dispatcher.setTrackChannel(trackId, change)
      if (!result.ok) log(result.message, 'error')
    },
    // At the playhead: the cut goes where the music is, and says so when it is not on the clip.
    onSplit: id => {
      try {
        const at = playheadBeat()
        edit(splitClip(ctx.dispatcher.project, id, at, { transport: ctx.dispatcher.transport() }))
      } catch (error) { log(`${error.message}. Move the playhead onto the clip to cut it.`, 'error') }
    },
    onDuplicate: id => {
      try { edit(duplicateClip(ctx.dispatcher.project, id)) } catch (error) { log(error.message, 'error') }
    },
    onTrim: (id, edge) => {
      try {
        const at = playheadBeat()
        edit(trimClip(ctx.dispatcher.project, id, edge === 'start' ? { from: at } : { to: at }, { transport: ctx.dispatcher.transport() }))
      } catch (error) { log(`${error.message}. Move the playhead onto the clip to trim it.`, 'error') }
    },
    onCopy: id => {
      try {
        // The selected clips when this one is among them, otherwise just this one.
        const ids = ctx.selection.has('clip', id) ? ctx.selection.ids : [id]
        clipboard = copyClips(ctx.dispatcher.project, ids)
        log(`copied ${ids.length} clip${ids.length === 1 ? '' : 's'}`, 'ok')
        drawClipActions()
      } catch (error) { log(error.message, 'error') }
    },
    // Copy, then remove, as one edit so one undo puts them back.
    onCut: id => {
      try {
        const ids = ctx.selection.has('clip', id) ? ctx.selection.ids : [id]
        clipboard = copyClips(ctx.dispatcher.project, ids)
        edit(ids.map(clipId => ({ op: 'removeClip', id: clipId })))
        log(`cut ${ids.length} clip${ids.length === 1 ? '' : 's'}`, 'ok')
      } catch (error) { log(error.message, 'error') }
    },
    onPaste: id => {
      try {
        const { project } = ctx.dispatcher
        // One clip goes onto the track of the clip that has focus; a group goes back where it came from.
        const track = clipboard?.length === 1 ? project.clip(id)?.track : null
        edit(pasteClips(project, clipboard, { startBeat: playheadBeat(), track }))
      } catch (error) { log(error.message, 'error') }
    },
    onLock: (id, locked) => edit([{ op: 'setClip', id, locked }]),
    onMoveInChain: (nodeId, delta) => {
      try {
        const passesAudio = id => {
          const profile = ctx.dispatcher.engineNode(id)?.profile
          return (profile?.audioInputs ?? 0) > 0 && (profile?.audioOutputs ?? 0) > 0
        }
        edit(chainSwapChanges(ctx.dispatcher.project, nodeId, delta, { passesAudio }))
      } catch (error) { log(error.message, 'error') }
    },
    // An automation lane edited: the whole new list of points, one edit and one undo.
    onEnvelope: (id, points) => edit([{ op: 'setEnvelope', id, points }]),
    onRemoveEnvelope: id => edit([{ op: 'removeEnvelope', id }]),
    // A lane for the master's level or pan, starting at what it is now.
    onAutomateMaster: kind => {
      const { master } = ctx.dispatcher.project
      const value = kind === 'masterGain' ? master.gain : kind === 'masterPan' ? master.pan : ctx.dispatcher.project.transport.tempoPoints[0]?.bpm ?? 120
      edit([{ op: 'addEnvelope', target: { kind }, points: [{ atBeat: 0, value, curve: 'linear' }] }])
    },
    onReplace: nodeId => ctx.swap.begin(nodeId),
    // Double click on a track's name: the editor already shows the track, so the focus goes to its name field.
    onEditTrack: trackId => {
      ctx.selection.set('track', [trackId])
      ctx.dock.update()
      const field = document.getElementById('track-name-input')
      field?.focus()
      field?.select()
    },
    onDeleteTrack: trackId => ctx.dock.deleteTrack(trackId),
    // The same Op as Remove on the Plugins tab, healing the chain where the plugin stood.
    onRemoveNode: nodeId => {
      const label = ctx.dispatcher.project.node(nodeId)?.label ?? nodeId
      const result = edit([{ op: 'removeNode', id: nodeId, heal: true }])
      if (result.ok) { ctx.rack.forgetNode(nodeId); log(`removed ${label}`) }
    },
    // Show a plugin's own controls: the Plugins tab, at its track.
    onShowNode: nodeId => {
      ctx.tabs.select('tracks')
      const track = ctx.dispatcher.project.node(nodeId)?.track
      document.getElementById(`track-group-${track}`)?.scrollIntoView({ block: 'start' })
    },
    onBypass: (nodeId, bypassed) => edit([{ op: 'setNode', id: nodeId, bypassed }]),
    onMute: (id, muted) => edit([{ op: 'setClip', id, muted }]),
    onRemove: id => {
      if (pianoRoll.clipId === id) pianoRoll.hide()
      edit([{ op: 'removeClip', id }])
    }
  }
  const timeline = createTimeline(document, handlers, { selection: ctx.selection, menu: ctx.menu })

  const clipActions = createClipActions(document, {
    // Editor metadata: no revision, no undo, like a track's colour.
    onColor: color => {
      for (const id of ctx.selection.kind === 'clip' ? ctx.selection.ids : []) ctx.dispatcher.project.setClipColor(id, color)
      ctx.rack.draw()
    },
    onAction: name => {
      const ids = ctx.selection.kind === 'clip' ? ctx.selection.ids : []
      if (ids.length === 0) return
      switch (name) {
        case 'split': return handlers.onSplit(ids[0])
        case 'trimStart': return handlers.onTrim(ids[0], 'start')
        case 'trimEnd': return handlers.onTrim(ids[0], 'end')
        case 'duplicate': return edit(ids.flatMap(id => duplicateClip(ctx.dispatcher.project, id)))
        case 'mute': {
          const muted = !ids.every(id => ctx.dispatcher.project.clip(id)?.muted)
          return edit(ids.map(id => ({ op: 'setClip', id, muted })))
        }
        case 'lock': {
          const locked = !ids.every(id => ctx.dispatcher.project.clip(id)?.locked)
          return edit(ids.map(id => ({ op: 'setClip', id, locked })))
        }
        case 'copy': return handlers.onCopy(ids[0])
        case 'cut': return handlers.onCut(ids[0])
        case 'paste': return handlers.onPaste(ids[0])
        case 'remove': return edit(ids.map(id => ({ op: 'removeClip', id })))
      }
    }
  })
  function drawClipActions () {
    const project = ctx.dispatcher?.project
    const ids = ctx.selection.kind === 'clip' ? ctx.selection.ids.filter(id => project?.clip(id)) : []
    clipActions.draw({
      count: ids.length,
      allMuted: ids.length > 0 && ids.every(id => project.clip(id).muted),
      allLocked: ids.length > 0 && ids.every(id => project.clip(id).locked),
      canPaste: clipboard !== null
    })
  }
  ctx.selection.subscribe(drawClipActions)

  // What Ctrl+C took: plain data, kept here so it outlasts the clips and an undo.
  let clipboard = null

  /** The automation lanes of a track: an envelope on each parameter of each plugin on it, named and ranged from the profile. */
  function automationLanes (project, track) {
    const nodeIds = new Set(project.nodes.filter(n => n.track === track.id).map(n => n.id))
    const { beatsPerBar } = project.transport
    return project.envelopes.filter(e => e.target.node !== undefined && nodeIds.has(e.target.node)).flatMap(envelope => {
      const node = project.node(envelope.target.node)
      const profile = ctx.dispatcher.engineNode(node.id)?.profile
      const port = profile?.ports?.find(p => p.symbol === envelope.target.symbol)
      // A plugin that has not loaded, or no longer has the parameter, cannot be drawn against a range.
      if (!port || !(port.maximum > port.minimum)) return []
      return [{
        id: envelope.id,
        label: `${node.label ?? profile.label ?? node.id}, ${port.name || port.symbol}`,
        min: port.minimum,
        max: port.maximum,
        start: node.settings.get(port.symbol) ?? port.defaultValue,
        points: envelope.points,
        speak: value => spokenValue(port, value),
        where: beat => barBeat(beat, beatsPerBar)
      }]
    })
  }

  /** The master's and the tempo's automation: a lane for each that has an envelope, and what may still be added. */
  function masterLanes (project) {
    const { beatsPerBar } = project?.transport ?? { beatsPerBar: 4 }
    if (!project) return { list: [], available: [] }
    const kinds = {
      masterGain: { label: 'Master level', min: 0, max: 2, start: project.master.gain, speak: v => (v > 0 ? `${(20 * Math.log10(v)).toFixed(1)} decibels` : 'silent') },
      tempo: { label: 'Tempo', min: 20, max: 300, start: project.transport.tempoPoints[0]?.bpm ?? 120, speak: v => `${Math.round(v)} beats a minute` },
      masterPan: { label: 'Master pan', min: -1, max: 1, start: project.master.pan, speak: v => (v === 0 ? 'centre' : `${Math.abs(v).toFixed(2)} ${v < 0 ? 'left' : 'right'}`) }
    }
    const has = new Set(project.envelopes.map(e => e.target.kind).filter(Boolean))
    return {
      list: project.envelopes.filter(e => kinds[e.target.kind]).map(e => ({
        id: e.id, ...kinds[e.target.kind], points: e.points, where: beat => barBeat(beat, beatsPerBar)
      })),
      available: Object.entries(kinds).filter(([kind]) => !has.has(kind)).map(([kind, k]) => ({ kind, label: k.label }))
    }
  }

  /** The playhead's beat, on the grid the timeline is set to (or exactly, with snapping off). */
  const playheadBeat = () => timeline.view.snap(ctx.transport.position().beat, ctx.dispatcher.project.transport.beatsPerBar)

  /**
   * Sound a note through the open clip's track, as it is placed or chosen: a
   * quarter of a second, at the stream position the clock has reached. Into
   * the track's MIDI input, where the clip itself plays, and into nothing on a
   * track without one, which is also what the clip would do.
   */
  function audition (pitch, velocity) {
    const { dispatcher, engine } = ctx
    const clip = dispatcher?.project.clip(pianoRoll.clipId)
    const nodeId = clip && dispatcher.project.track(clip.track)?.midiInput
    if (!nodeId || !engine) return
    const { currentTime, sampleRate } = engine.context
    const frame = Math.round(currentTime * sampleRate)
    dispatcher.sendEvents(nodeId, [
      { frame, bytes: Uint8Array.from([0x90, pitch, velocity]) },
      { frame: frame + Math.round(0.25 * sampleRate), bytes: Uint8Array.from([0x80, pitch, 0]) }
    ])
  }

  const pianoRoll = createPianoRoll(document, {
    // A note drawn past the clip's end brings the clip's new length with it,
    // and both go as one edit, so one undo takes back both.
    onChange: (id, notes, { lengthBeats } = {}) => edit([
      ...(lengthBeats === undefined ? [] : [{ op: 'setClip', id, lengthBeats }]),
      { op: 'setClipNotes', id, notes }
    ]),
    onAudition: audition,
    // The events of a clip replace as one edit, like its notes.
    onControllers: (id, controllers) => edit([{ op: 'setClipControllers', id, controllers }]),
    onLoop: (id, loopBeats) => edit([{ op: 'setClip', id, loopBeats }]),
    onTransform: (name, options, { indices = null } = {}) => {
      let changes
      try { changes = describeNoteTransform(ctx.dispatcher.project, pianoRoll.clipId, name, options, { indices }) } catch (error) { log(error.message, 'error'); return error.message }
      const result = edit(changes)
      return result.ok ? undefined : result.message
    },
    // A rhythm laid into the open clip: one edit, so one undo. Returns the reason if it was refused.
    onEuclid: (id, options) => {
      let changes
      try { changes = describeEuclidean(ctx.dispatcher.project, id, options) } catch (error) { return error.message }
      const result = edit(changes)
      return result.ok ? undefined : result.message
    },
    onClose: () => {
      const id = pianoRoll.clipId
      pianoRoll.hide()
      ctx.selection.clear()
      ctx.dock.update()
      document.getElementById(`clip-${id}`)?.focus()
    }
  })

  /** Select a clip and let the dock show it. */
  function openClip (id) {
    ctx.selection.set('clip', [id])
    ctx.dock.update()
  }

  /** A clip's notes, in the piano roll, for a person to edit. */
  function showInRoll (id) {
    const { project } = ctx.dispatcher
    const clip = project.clip(id)
    if (!clip || clip.kind !== 'midi') return
    const track = project.tracks.find(t => t.id === clip.track)
    pianoRoll.show(clip, { beatsPerBar: project.transport.beatsPerBar, label: ctx.rack.trackLabel(track, project.tracks.indexOf(track)) })
    // The dock sits below the timeline, off screen on a short window, so opening the roll looked like nothing happened.
    pianoRoll.element.scrollIntoView?.({ block: 'nearest' })
  }

  /**
   * The timeline, and the piano roll if a clip is open in it. Redrawn on every
   * change like the rack, with the focus put back on the same clip; the piano
   * roll keeps its own cursor and focus across a redraw.
   */
  function draw (tracks, labelOfTrack) {
    const project = ctx.dispatcher?.project
    const { clipPlayer } = ctx
    const restore = preserveFocus(timeline.element)
    const nodes = project?.nodes ?? []
    const latencies = new Map((ctx.dispatcher?.trackLatencies() ?? []).map(l => [l.trackId, l]))
    const sampleRate = ctx.engine?.context.sampleRate ?? null
    const silent = new Map((ctx.dispatcher?.audibility() ?? []).map(a => [a.trackId, a.silent]))
    timeline.draw({
      tracks,
      // The same two questions the Mixer tab asks, so a strip there and a
      // header here agree on which tracks have anything to hear.
      mixable: track => mixable(track, nodes, id => ctx.dispatcher.engineNode(id)?.profile?.audioOutputs),
      silent: track => silent.get(track.id) === true,
      clips: project?.arrangementClips ?? [],
      beatsPerBar: project?.transport.beatsPerBar ?? 4,
      // What to do first, as buttons over the requests the page already makes.
      empty: {
        text: 'Nothing here yet. Play a tune, start something new, or open a piece you saved.',
        groups: describeStart({
          tunes: ctx.sessions.presetNames(),
          templates: ctx.templates.names(),
          run: {
            tune: name => ctx.sessions.openPreset(name),
            template: name => ctx.templates.open(name).catch(error => log(error.message, 'error')),
            emptyTrack: () => addTrack(),
            sound: () => ctx.addTrack.open(),
            loops: () => ctx.loops.open(),
            browse: () => ctx.layout.showBrowser(true),
            open: () => document.getElementById('openfile').click()
          }
        })
      },
      layoutFor: track => project.trackLayout(track.id),
      lanesFor: track => automationLanes(project, track),
      masterFor: () => masterLanes(project),
      colorFor: clip => project.clipColor(clip.id),
      chainFor: track => describeChain(project, track.id, {
        profileOf: id => ctx.dispatcher.engineNode(id)?.profile,
        labelOf: id => { const n = project.node(id); return n?.label ?? ctx.dispatcher.engineNode(id)?.profile?.label ?? id },
        failedOf: id => ctx.dispatcher.engineNode(id)?.failed ?? null,
        trackLabelOf: id => ctx.rack.trackLabel(project.track(id), project.tracks.indexOf(project.track(id)))
      }),
      routingFor: track => describeRouting(project, track.id, id => ctx.rack.trackLabel(project.track(id), project.tracks.indexOf(project.track(id)))),
      kindFor: track => trackKind(project, track, id => ctx.dispatcher.engineNode(id)?.profile),
      canArm: () => true,
      armed: track => ctx.midiIn.armed(track.id) || ctx.micIn?.armed?.(track.id) === true,
      armSource: track => track.recordSource ?? (track.audioInput && !track.midiInput ? 'audio' : 'midi'),
      latencyFor: track => {
        const found = latencies.get(track.id)
        return found === undefined ? null : { frames: found.frames, alignFrames: found.alignFrames, rate: sampleRate }
      },
      loop: project ? { start: project.transport.loopStart, end: project.transport.loopEnd, enabled: project.transport.loopEnabled } : null,
      labelFor: labelOfTrack,
      playsIntoNothing: track => !track.midiInput,
      peaksFor: (clip, count) => {
        if (!clipPlayer) return null
        const peaks = clipPlayer.peaks(clip.source, count)
        if (!peaks && !waveformsRequested.has(clip.source)) {
          waveformsRequested.add(clip.source)
          // Asked once, so a file that will not load does not ask again on
          // every redraw. Drawn again either way: with its waveform, or saying
          // it cannot play.
          clipPlayer.load(clip.source).then(() => ctx.rack.draw())
        }
        return peaks
      },
      unplayable: clip => clipPlayer?.failure(clip.source)?.message ?? null
    })
    restore()
    fitTimeline()
    if (pianoRoll.clipId) {
      const clip = project?.clip(pianoRoll.clipId)
      if (clip) pianoRoll.draw(clip)
      else pianoRoll.hide()
    }
    // A clip or track that has gone leaves the selection, and the dock follows.
    ctx.selection.prune((kind, id) => (kind === 'clip' ? project?.clip(id) : kind === 'node' ? project?.node(id) : project?.track(id)) != null)
    ctx.dock.update()
    drawClipActions()
  }

  /**
   * Take an audio file a person chose into the session: kept under the
   * session's IRI by its SHA-256, so the same file imported twice is one file,
   * decoded to learn its length, and placed as a clip that long.
   */
  async function importAudio (file, { trackId, startBeat }) {
    await ctx.runtime.ensureRunning()
    if (!file.type.startsWith('audio/')) { log(`${file.name} is not an audio file`, 'error'); return }
    const bytes = new Uint8Array(await file.arrayBuffer())
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
    const hex = [...digest].map(b => b.toString(16).padStart(2, '0')).join('')
    const extension = (file.name.match(/\.([a-z0-9]+)$/i)?.[1] ?? 'audio').toLowerCase()
    const iri = ctx.media.iriFor(hex, extension)
    ctx.media.put(iri, bytes, file.type)
    const buffer = await ctx.clipPlayer.load(iri)
    if (!buffer) { log(`${file.name}: ${ctx.clipPlayer.failure(iri).message}`, 'error'); return }
    const transport = ctx.dispatcher.transport()
    const from = transport.secondsAtBeat(startBeat)
    const lengthBeats = transport.beatAtSeconds(from + buffer.duration) - startBeat
    const result = edit([{ op: 'addClip', track: trackId, kind: 'audio', startBeat, lengthBeats, source: iri, offsetSeconds: 0 }])
    if (result.ok) log(`added ${file.name}, ${buffer.duration.toFixed(2)} seconds`, 'ok')
  }

  /** Wire the file picker and put the timeline and piano roll on the page. */
  /**
   * Make the box the lanes scroll in end where the window does, so its horizontal scrollbar is at the bottom of the screen
   * and not down the page past every track. It is measured from where the box is, because what is above it (the transport
   * bar wraps, the tabs, the tools) is not a number the stylesheet can know. A hidden box measures nothing and is left.
   */
  function fitTimeline () {
    const box = timeline.element.querySelector?.('.timeline-scroll') ?? timeline.element
    if (!box || box.getClientRects().length === 0) return
    const top = box.getBoundingClientRect().top + window.scrollY
    box.style.maxHeight = `${Math.max(192, Math.floor(window.innerHeight - top - 12))}px`
  }

  function mount () {
    window.addEventListener('resize', fitTimeline)
    // The page's own height changes when the browser panel opens, a tab is chosen or the toolbar wraps.
    new ResizeObserver(fitTimeline).observe(document.body)
    $('audiofile').addEventListener('change', event => {
      const file = event.target.files?.[0]
      event.target.value = ''
      const target = pendingAudio
      pendingAudio = null
      if (!file || !target) return
      importAudio(file, target).catch(error => log(`${file.name}: ${error.message}`, 'error'))
    })
    $('timeline-mount').append(clipActions.element, timeline.element)
    ctx.dock.slot('midi').append(pianoRoll.element)
    $('dock-mount').append(ctx.dock.element)
  }

  return {
    draw,
    mount,
    addTrack,
    /** Show where the transport is, as a beat, or null when it is stopped. */
    playhead (beat) { timeline.playhead(beat); pianoRoll.playhead(beat) },
    /** Forget what was drawn for the session being replaced. */
    reset () { pianoRoll.hide(); ctx.selection.clear(); waveformsRequested.clear(); ctx.launcher?.reset() },
    showInRoll,
    hideRoll: () => pianoRoll.hide(),
    get rollClipId () { return pianoRoll.clipId }
  }
}
